import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import type { FilterOption } from '../common-table/FilterSearchBar'
import type { CustomViewsConfig } from './CustomViewsContext'
import { getFilteredLengthForView } from './CustomViewsDropdown'

type CustomViewTabsProps = {
	visibleCustomViews: { id: string | number; name: string; filters: unknown }[] // TODO: Replace 'unknown' in filters with the actual filter type if known
	tabsToDisplay: unknown[] // TODO: Replace 'unknown' with the actual type if known
	activeViewId: string | number
	handleClearView: () => void
	handleSelectView: (id: string | number) => void
	// handleEditView?: (view: { id: string | number; name: string; filters: FilterOption[] }) => void
	handleDeleteView: (id: string | number) => void
	setActiveIndex: (index: number) => void
	tabRefs: React.MutableRefObject<(HTMLElement | null)[]>
	config: CustomViewsConfig | null
	/** Starting index in tabRefs for custom view tabs. Defaults to tabsToDisplay.length. */
	baseIndex?: number
}

export function CustomViewTabs({
	visibleCustomViews,
	tabsToDisplay,
	activeViewId,
	handleSelectView,
	// handleEditView,
	handleDeleteView,
	setActiveIndex,
	tabRefs,
	config,
	baseIndex,
}: CustomViewTabsProps) {
	const effectiveBaseIndex = baseIndex ?? tabsToDisplay.length
	const [contextMenu, setContextMenu] = useState<{
		view: { id: string | number; name: string; filters: unknown } | null
		x: number
		y: number
	}>({ view: null, x: 0, y: 0 })

	return (
		<>
			{visibleCustomViews.map((view, viewIndex) => {
				const actualIndex = effectiveBaseIndex + viewIndex
				const isActive = String(activeViewId) === String(view.id)

				const setTabRef = (el: HTMLElement | null) => {
					tabRefs.current[actualIndex] = el
				}

				return (
					<Button
						key={view.id}
						ref={setTabRef}
						variant='ghost'
						size='sm'
						className={cn(
							'h-control border-none text-body ml-2 rounded-[6px] hover:text-foreground relative z-10 transition-colors duration-150 ease-in-out',
							isActive ? 'text-header-tab-active-text' : 'bg-transparent text-header-tab-inactive-text',
						)}
						onClick={() => {
							setActiveIndex(actualIndex)
							handleSelectView(view.id)
						}}
						onContextMenu={e => {
							// right-click handler
							e.preventDefault()
							e.stopPropagation()
							setContextMenu({
								view,
								x: e.clientX,
								y: e.clientY,
							})
						}}>
						<span className='text-body'>{view.name}</span>
						<span
							className={cn(
								'flex items-center justify-center w-5 h-5 rounded-full text-body ml-1',
								isActive ? 'bg-header-tab-active-counter-bg' : 'bg-header-tab-inactive-counter-bg',
							)}>
							{getFilteredLengthForView(config?.data ?? [], view.filters as FilterOption[])}
						</span>
					</Button>
				)
			})}

			{/* Custom dropdown that appears at mouse position */}
			<DropdownMenu
				open={!!contextMenu.view}
				onOpenChange={open => !open && setContextMenu({ view: null, x: 0, y: 0 })}>
				<DropdownMenuContent
					className='w-32'
					style={{
						position: 'fixed',
						left: contextMenu.x,
						top: contextMenu.y,
					}}>
					{/* Temporarily commenting Edit option as mentioned */}
					{/* 
					<DropdownMenuItem
						onClick={() => {
							if (contextMenu.view) {
								handleEditView?.({
									id: contextMenu.view?.id,
									name: contextMenu.view?.name,
									filters: contextMenu.view?.filters as FilterOption[],
								})
							}
							setContextMenu({ view: null, x: 0, y: 0 })
						}}>
						Edit
					</DropdownMenuItem> 
*/}
					<DropdownMenuItem
						onClick={() => {
							if (contextMenu.view) {
								handleDeleteView(contextMenu.view.id)
							}
							setContextMenu({ view: null, x: 0, y: 0 })
						}}
						className='text-destructive'>
						Delete
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</>
	)
}
